import { Controller } from '@hotwired/stimulus';

import CurrentPage from '@frontend/app/features/CurrentPage';
import {
  apply as queryParamsApply,
  extract as extractQueryParams,
} from '@frontend/utils/query-params';

// NOTE: JS and CSS from https://css-tricks.com/lets-create-a-custom-audio-player/ has been used to
// work around some browser-specific quirks.
export default class extends Controller {
  static targets = [
    'jumpBackButton',
    'jumpForwardButton',
    'playButton',
    'seekSlider',
    'audio',
    'duration',
    'currentTime',
    'playbackSpeedSelect',
  ];

  static values = {
    srcUrl: String,
    durationInSeconds: Number,
    playbackSpeed: {
      type: Number,
      default: 1,
    },
  };

  connect() {
    if (CurrentPage.getShareUrl().isActive) {
      const shareUrl = CurrentPage.getShareUrl().url;
      const newSrcUrl = queryParamsApply(
        this.srcUrlValue,
        extractQueryParams(shareUrl)
      );
      this.srcUrlValue = newSrcUrl;
      this.setServingViaShareUrl(true);
    } else {
      this.setServingViaShareUrl(false);
    }

    this.audioTarget.src = this.srcUrlValue;
    this.isPlaying = false; // Autoplay is disabled
    this.jumpingInSeconds = 15;

    this.audioTarget.addEventListener(
      'progress',
      this.displayBufferedAmount.bind(this)
    );
    this.audioTarget.addEventListener('ended', this.audioEnded.bind(this));
    this.audioTarget.addEventListener('playing', this.audioPlaying.bind(this));

    this.playButtonTarget.addEventListener(
      'click',
      this.playPressed.bind(this)
    );
    this.seekSliderTarget.addEventListener(
      'input',
      this.seekSliderInput.bind(this)
    );
    this.seekSliderTarget.addEventListener(
      'change',
      this.seekSliderChanged.bind(this)
    );
    this.jumpBackButtonTarget.addEventListener(
      'click',
      this.jumpBack.bind(this)
    );
    this.jumpForwardButtonTarget.addEventListener(
      'click',
      this.jumpForward.bind(this)
    );

    this.playbackSpeedSelectTarget.addEventListener(
      'change',
      this.playbackSpeedSelectChanged.bind(this)
    );

    this.interval = setInterval(this.whilePlaying.bind(this), 500);
    this.setup();
    // NOTE: Calling setup after loading metadata to use the audio duration from the metadata.
    this.audioTarget.addEventListener('loadedmetadata', this.setup.bind(this));
  }

  disconnect() {
    clearInterval(this.interval);
  }

  setup() {
    this.displayDuration();
    this.setSliderMax();
    this.displayBufferedAmount();
  }

  audioEnded() {
    this.togglePlayState();
  }

  audioPlaying() {
    document.dispatchEvent(new CustomEvent('audio-player:playing'));
  }

  playPressed() {
    this.togglePlayState();
  }

  jumpBack() {
    this.audioTarget.currentTime =
      this.audioTarget.currentTime - this.jumpingInSeconds;
  }

  jumpForward() {
    this.audioTarget.currentTime =
      this.audioTarget.currentTime + this.jumpingInSeconds;
  }

  togglePlayState() {
    this.isPlaying = !this.isPlaying;

    if (this.isPlaying) {
      this.audioTarget.play();
    } else {
      this.audioTarget.pause();
      document.dispatchEvent(new CustomEvent('audio-player:stopped'));
    }

    this.element.classList.toggle('is-playing', this.isPlaying);
  }

  seekSliderInput() {
    this.updateSeekSliderProgress();
    this.currentTimeTarget.textContent = this.calculateTime(
      this.seekSliderTarget.value
    );
  }

  playbackSpeedSelectChanged() {
    this.playbackSpeedValue = parseFloat(this.playbackSpeedSelectTarget.value);
  }

  playbackSpeedValueChanged() {
    this.audioTarget.playbackRate = this.playbackSpeedValue;
    this.playbackSpeedSelectTarget.value = this.playbackSpeedValue;
  }

  seekSliderChanged() {
    this.audioTarget.currentTime = this.seekSliderTarget.value;
    if (!this.audioTarget.paused) {
      this.whilePlaying();
    }
  }

  whilePlaying() {
    const playbackTimeInSeconds = Math.floor(this.audioTarget.currentTime);
    this.seekSliderTarget.value = playbackTimeInSeconds;
    this.currentTimeTarget.textContent = this.calculateTime(
      playbackTimeInSeconds
    );
    this.updateSeekSliderProgress();
  }

  calculateTime(secs) {
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${minutes}:${returnedSeconds}`;
  }

  updateSeekSliderProgress() {
    this.setAudioPlayerProperty(
      'seek-before-width',
      this.percentageAsText(
        this.seekSliderTarget.value,
        this.seekSliderTarget.max
      )
    );
  }

  displayDuration() {
    this.durationTarget.textContent = this.calculateTime(
      this.getDurationInSeconds()
    );
  }

  setSliderMax() {
    this.seekSliderTarget.max = Math.floor(this.audioTarget.duration);
  }

  getDurationInSeconds() {
    return this.audioTarget.duration || this.durationInSecondsValue;
  }

  displayBufferedAmount() {
    if (this.audioTarget.buffered.length === 0) {
      return false;
    }

    const bufferedAmount = Math.floor(
      this.audioTarget.buffered.end(this.audioTarget.buffered.length - 1)
    );
    this.setAudioPlayerProperty(
      'buffered-width',
      this.percentageAsText(bufferedAmount, this.seekSliderTarget.max)
    );
  }

  setAudioPlayerProperty(propertyName, value) {
    this.element.style.setProperty(`--${propertyName}`, value);
  }

  percentageAsText(divided, divisor) {
    return `${(divided / divisor) * 100}%`;
  }

  setServingViaShareUrl(bool) {
    this.element.dataset.testAudioPlayerIsServingViaShareUrl = bool;
  }
}
